import {
  Button,
  Center,
  Divider,
  Group,
  Loader,
  Popover,
  PopoverProps,
  ScrollArea,
  Stack,
  Text,
  TextInput,
  Tooltip,
  Transition,
  UnstyledButton,
  useMantineTheme,
} from '@mantine/core';
import { faCircleCheck as faCircleCheckSolid } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition, faCircleCheck, faSearch } from '@fortawesome/pro-regular-svg-icons';
import { If } from '../If';
import { KeyValue } from '@vision/ui/interfaces';
import { CSSProperties, PropsWithChildren, useEffect, useMemo, useRef, useState } from 'react';
import { normalizeDiacritics, truncateString } from '@vision/ui/utils';
import { useDebouncedValue, useInputState } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';
import classes from './PopoverSelect.module.css';
import clsx from 'clsx';

interface PopoverSelectDropdownScrollPosition {
  scrollY: number;
  scrollHeight: number;
}

export interface PopoverSelectProps extends PopoverProps {
  dropdownTestId?: string;
  dropdownPadding?: number | string;
  getItemLeftSection?: (item: KeyValue) => React.ReactNode;
  getItemTestId?: (item: KeyValue) => string;
  itemClassName?: string;
  itemStyle?: CSSProperties;
  icon: IconDefinition;
  items: KeyValue[];
  loading?: boolean;
  maxDropdownHeight?: number;
  onScrollToBottom?: VoidFunction;
  onSearchChange?: (text: string) => void;
  onSelect: (item: KeyValue) => void;
  onSelectAll?: (items: KeyValue[]) => void;
  padding?: number | string;
  search?: boolean;
  title?: string;
  tooltipEnabled?: boolean;
  value?: KeyValue | KeyValue[];
  withSelectAll?: boolean;
  withSubValue?: boolean;
  showSelectedOnTop?: boolean;
}

export function PopoverSelect({
  children,
  dropdownTestId,
  dropdownPadding,
  getItemLeftSection,
  getItemTestId,
  itemClassName,
  itemStyle,
  icon,
  items,
  loading,
  maxDropdownHeight = 150,
  onScrollToBottom,
  onSearchChange,
  onSelect,
  onSelectAll,
  padding = 10,
  search = true,
  title,
  tooltipEnabled = true,
  value,
  width = 360,
  withSelectAll = true,
  withSubValue = false,
  showSelectedOnTop = false,
  ...props
}: PropsWithChildren<PopoverSelectProps>) {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const viewport = useRef<HTMLDivElement>(null);
  const [searchText, setSearchText] = useInputState('');
  const [currentScrollPosition, setCurrentScrollPosition] = useState<PopoverSelectDropdownScrollPosition>();

  const [debouncedScrollPosition] = useDebouncedValue(currentScrollPosition, 200);

  const searchableItems = useMemo(() => {
    if (!search || !searchText) {
      return items ?? [];
    }

    const normalizedText = normalizeDiacritics(searchText)?.toLocaleLowerCase('tr-TR');

    return (items ?? []).filter((item) => {
      const searchableText = withSubValue ? `${item.value} ${item.subValue ?? ''}` : item.value;
      const normalizedValue = normalizeDiacritics(searchableText)?.toLocaleLowerCase('tr-TR');
      return normalizedValue?.includes(normalizedText);
    });
  }, [items, search, searchText, withSubValue]);

  const sortedParsedData = useMemo(() => {
    if (!showSelectedOnTop) return searchableItems;

    const selectedItems = Array.isArray(value)
      ? searchableItems.filter((item) => value.some((selectedItem) => selectedItem.key === item.key))
      : searchableItems.filter((item) => item.key === value?.key);

    const unselectedItems = searchableItems.filter(
      (item) => !selectedItems.some((selectedItem) => selectedItem.key === item.key),
    );

    return [...selectedItems, ...unselectedItems];
  }, [searchableItems, showSelectedOnTop, value]);

  useEffect(() => {
    if (!search) {
      return;
    }

    onSearchChange?.(searchText);
  }, [onSearchChange, searchText, search]);

  useEffect(() => {
    if (!search) {
      setSearchText('');
    }
  }, [search, setSearchText]);

  const handleSelectAll = () => {
    if (onSelectAll) {
      onSelectAll(searchableItems);
    }

    setSearchText('');
  };

  const handleOnScrollPositionChange = ({ y }: { x: number; y: number }) => {
    const scrollHeight = viewport.current.scrollHeight - Number(maxDropdownHeight);
    const verticalPosition = Math.ceil(y);
    setCurrentScrollPosition({
      scrollY: verticalPosition,
      scrollHeight,
    });
  };

  useEffect(() => {
    if (debouncedScrollPosition?.scrollY === debouncedScrollPosition?.scrollHeight) {
      onScrollToBottom?.();
    }
  }, [debouncedScrollPosition]);

  return (
    <Popover
      classNames={{
        dropdown: classes.popoverSelect,
      }}
      position="bottom-end"
      offset={{
        mainAxis: -30,
      }}
      withArrow={false}
      width={width}
      {...props}
    >
      <Popover.Target>
        {children ?? (
          <Button
            data-testid="input-popover-select-target"
            size="compact-sm"
            variant="subtle"
            disabled={items?.length === 0}
            leftSection={<FontAwesomeIcon icon={icon} />}
          >
            {title}
          </Button>
        )}
      </Popover.Target>
      <Popover.Dropdown p={dropdownPadding} data-testid={dropdownTestId}>
        <Stack gap={10}>
          <If value={!!title}>
            <Group gap={10}>
              <FontAwesomeIcon icon={icon} color={theme.colors.blue[5]} />
              <Text c="blue" fw={500}>
                {title}
              </Text>
            </Group>
          </If>

          <If value={search}>
            <TextInput
              classNames={{ input: classes.searchInput }}
              data-testid="input-popover-select-search"
              placeholder={t('search')}
              rightSection={<FontAwesomeIcon icon={faSearch} />}
              value={searchText}
              onChange={setSearchText}
              size="32"
            />
          </If>

          <ScrollArea.Autosize
            mah={maxDropdownHeight}
            type="auto"
            viewportRef={viewport}
            onScrollPositionChange={handleOnScrollPositionChange}
            classNames={{ root: classes.ScrollArea, scrollbar: classes.scrollbar }}
          >
            <Stack>
              <If value={withSelectAll}>
                <Transition mounted={onSelectAll && !!items?.length} transition="slide-down" exitDuration={400}>
                  {(styles) => {
                    return (
                      <Stack style={styles}>
                        <UnstyledButton
                          variant="subtle"
                          data-testid="button-select-all"
                          key="selectAll"
                          size="compact-xs"
                          onClick={handleSelectAll}
                        >
                          <Group className={classes.popoverOverlayItem} p={padding}>
                            <Text size="xs" c="gray" fw={400}>
                              {t('selectAll')}
                            </Text>
                          </Group>
                        </UnstyledButton>
                        <Divider my={5} />
                      </Stack>
                    );
                  }}
                </Transition>
              </If>

              {sortedParsedData?.map((item) => {
                const selected = Array.isArray(value)
                  ? value?.some((selectedItem) => selectedItem.key === item.key)
                  : value?.key === item.key;
                const itemLeftSection = getItemLeftSection?.(item);
                const hasSubValue = withSubValue && !!item.subValue;
                const tooltipLabel = hasSubValue ? `${item.value}\n${item.subValue}` : item.value;
                const itemText = hasSubValue ? (
                  <Stack gap={2} className={classes.popoverOverlayItemText}>
                    <Text size="sm" fw={500}>
                      {truncateString(item.value, 35)}
                    </Text>
                    <Text size="xs" c="dimmed" fw={400}>
                      {truncateString(item.subValue ?? '', 40)}
                    </Text>
                  </Stack>
                ) : (
                  <Text size="xs" fw={500} className={classes.popoverOverlayItemText}>
                    {truncateString(item.value, 35)}
                  </Text>
                );
                const content = tooltipEnabled ? <Tooltip label={tooltipLabel}>{itemText}</Tooltip> : itemText;

                return (
                  <UnstyledButton data-testid={getItemTestId?.(item)} key={item.key} onClick={() => onSelect(item)}>
                    <Group
                      className={clsx(classes.popoverOverlayItem, itemClassName, {
                        [classes.popoverOverlayItemSelected]: selected,
                      })}
                      style={itemStyle}
                      gap={0}
                      p={padding}
                      wrap="nowrap"
                    >
                      <If value={!!itemLeftSection}>
                        <div className={classes.popoverOverlayItemLeftSection}>{itemLeftSection}</div>
                      </If>

                      {content}

                      <FontAwesomeIcon
                        className={clsx(classes.popoverOverlayItemIcon, classes.popoverOverlayItemIconRight)}
                        icon={selected ? faCircleCheckSolid : faCircleCheck}
                      />
                    </Group>
                  </UnstyledButton>
                );
              })}

              <If value={loading}>
                <Center py="xs">
                  <Loader data-testid="section-popover-select-loading" size="sm" />
                </Center>
              </If>
            </Stack>
          </ScrollArea.Autosize>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}
